// GT1-488 — the payment-rail vocabulary and the availability predicate.
//
// Shell-owned because four surfaces gate on it — checkout's
// PaymentMethodSelector, registration's RequiredFieldsForm, the activation
// gate drawer, and the account payment-methods page — and features may not
// import each other. Kept out of the SFCs so the rule is unit-testable on its
// own: it is the one piece of logic here that is easy to get subtly wrong, and
// getting it wrong either offers a rail the processor cannot run or silently
// hides one the org paid for.
import type { MethodCapability, ProcessorCapabilities, PublicEvent } from '@neon-one/auctions-types';

/**
 * The wire's `PublicEvent.paymentProvider` block, verbatim.
 *
 * Replaces three divergent inline re-declarations that each dropped the
 * GT1-488 fields: `PaymentMethodSelector`'s `CheckoutProvider`,
 * `RequiredFieldsForm`'s `{ merchantId, publicKey }`, and
 * neon-pay-web's own `PaymentProvider`. The package type stays (it must not
 * depend on auctions-types) but it is now a strict structural subset, so the
 * wire object passes straight into every field component.
 */
export type PaymentProviderWire = NonNullable<PublicEvent['paymentProvider']>;

/** Rails rendered as a branded wallet button rather than a radio row. */
export type WalletRail = 'apple_pay' | 'google_pay' | 'paypal' | 'venmo';

export const WALLET_RAILS: readonly WalletRail[] = [
  'apple_pay',
  'google_pay',
  'paypal',
  'venmo',
] as const;

/** Every online rail this app can gate, wallets included. */
export type Rail = 'card' | 'ach' | WalletRail;

/**
 * `'unknown'` gates EXACTLY like `'supported'`.
 *
 * Slice 1 shipped tri-state capability with `'unknown'` as the conservative
 * default and slice 2 populates real values, but a capability read can still
 * fail (the backend returns all-unknown and deliberately does not cache it).
 * Treating `'unknown'` as unsupported would close checkout during a Neon Pay
 * or NeonCRM outage — only a positive `'unsupported'` hides a rail.
 */
export function capabilityAllows(capability: MethodCapability | undefined): boolean {
  return capability !== 'unsupported';
}

function capabilityFor(caps: ProcessorCapabilities | undefined, rail: Rail): MethodCapability {
  if (!caps) return 'unknown';
  switch (rail) {
    case 'card':
      return caps.card;
    case 'ach':
      return caps.ach;
    case 'apple_pay':
      return caps.applePay;
    case 'google_pay':
      return caps.googlePay;
    case 'paypal':
      return caps.paypal;
    case 'venmo':
      return caps.venmo;
  }
}

function toggleFor(provider: PaymentProviderWire, rail: Rail): boolean {
  switch (rail) {
    // Card has no org toggle — it is the baseline rail. `merchantConfigured`
    // is the card gate and is applied by railAvailable directly.
    case 'card':
      return true;
    case 'ach':
      return provider.achEnabled;
    case 'apple_pay':
      return provider.applePayEnabled;
    case 'google_pay':
      return provider.googlePayEnabled;
    case 'paypal':
      return provider.paypalEnabled;
    case 'venmo':
      return provider.venmoEnabled;
  }
}

export interface RailAvailabilityInput {
  /** Null in test mode and when no real merchant exists at all. */
  provider: PaymentProviderWire | null;
  /**
   * True whenever ANY general merchant exists, test-mode ones included
   * (GT1-411). Deliberately distinct from `provider !== null`.
   */
  merchantConfigured: boolean;
}

/**
 * Two gates, both required, exactly as the ticket specifies:
 *
 *   1. the org has the rail switched on (`*Enabled`)
 *   2. the merchant's processor can run it (`capability !== 'unsupported'`)
 *
 * Plus the test-mode rule: `provider === null` means either "test mode" or
 * "no merchant anywhere", and in both cases the only offer is card — via the
 * local mock tokenizer in test mode, or not at all. Admin Settings already
 * promises bidders "Card payments only — no ACH, Apple Pay, Google Pay,
 * PayPal, or Venmo in test mode", so nothing else may render, and we
 * deliberately do NOT build mocks for the other rails.
 */
export function railAvailable(input: RailAvailabilityInput, rail: Rail): boolean {
  const { provider, merchantConfigured } = input;
  if (!provider) return rail === 'card' && merchantConfigured;
  if (rail === 'card') {
    return (
      merchantConfigured && capabilityAllows(capabilityFor(provider.processorCapabilities, 'card'))
    );
  }
  return (
    toggleFor(provider, rail) &&
    capabilityAllows(capabilityFor(provider.processorCapabilities, rail))
  );
}

/** The processor name the ACH field needs to pick Stripe vs Payrix. */
export function processorOf(provider: PaymentProviderWire | null): string | null {
  return provider?.processorCapabilities?.processor ?? null;
}

/**
 * Gateway-mode merchant. Neon Pay issues Pay-Gateway merchants a `pk_` public
 * key instead of `public_`; Gateway supports card + bankaccount only, and the
 * SDK must be constructed with `{ gateway: true }` or every `createField` fails.
 *
 * Every `NeonPayBankAccountField` mount has to pass this through — a Gateway
 * merchant whose ACH field constructs `new NeonPay(key, id)` without the flag
 * cannot tokenize at all. Deliberately identical to admin-web's
 * `shell/lib/paymentRails.ts`: staff checkout and bidder checkout must answer
 * this the same way, and the key-prefix rule is the whole of it.
 */
export function isGatewayProvider(provider: PaymentProviderWire | null): boolean {
  return provider?.publicKey?.startsWith('pk_') ?? false;
}
